import { useCallback } from "react";

import useModalStore from "Flocknote/GlobalComponents/Modal/modalStore";
import * as peekHandleKeys from "Flocknote/GlobalComponents/PeekHandle/peekHandleKeys";
import { useModal } from "Flocknote/stateHooks";

import { fnReportError } from "Utilities/fnReportError";

import { usePeekHandleStore } from "./peekHandleStore";

const peekHandleKeysByValue = Object.fromEntries(
  Object.values(peekHandleKeys).map((elem) => [elem, elem])
);

/**
 * @typedef PeekHandleState
 * @property {boolean} isActive
 * @property {string} peekHandleKey
 */

/**
 * @typedef PeekHandleApi
 * @property {function} minimizeToPeekHandle - minimizes the current modal into a peek handle
 * @property {function} expandPeekHandle - restores the minimized modal and closes the peek handle
 */

/**
 * Returns state and interface to interact with the global PeekHandle component.
 *
 * minimizeToPeekHandle(key, peekHandleProps) snapshots the current modal state into the
 * peek handle store, then closes the modal. expandPeekHandle() restores the modal
 * from that snapshot.
 *
 * @returns {[PeekHandleState, PeekHandleApi]}
 */
const usePeekHandle = () => {
  const isActive = usePeekHandleStore((s) => s.isActive);
  const peekHandleKey = usePeekHandleStore((s) => s.peekHandleKey);
  const storeOpen = usePeekHandleStore((s) => s.open);
  const storeClose = usePeekHandleStore((s) => s.close);

  const [{ contentKey, contentProps }, { openModal, closeModal }] = useModal();

  const minimizeToPeekHandle = useCallback(
    (newKey, peekHandleProps = {}, contentPropsOverride) => {
      if (!peekHandleKeysByValue[newKey]) {
        fnReportError({
          message: `${newKey} was not found in Flocknote/GlobalComponents/PeekHandle/peekHandleKeys. Expected one of:\n\t${Object.values(
            peekHandleKeysByValue
          ).join("\n\t")}`,
        });
        return;
      }
      const mergedContentProps = contentPropsOverride
        ? { ...contentProps, ...contentPropsOverride }
        : contentProps;
      useModalStore.getState().setTransitionType("peekHandle");
      storeOpen(newKey, contentKey, mergedContentProps, peekHandleProps);
      closeModal();
    },
    [storeOpen, contentKey, contentProps, closeModal]
  );

  const expandPeekHandle = useCallback(() => {
    const { modalContentKey, modalContentProps } =
      usePeekHandleStore.getState();

    useModalStore.getState().setTransitionType("peekHandle");
    storeClose();
    openModal(modalContentKey, modalContentProps);
  }, [storeClose, openModal]);

  return [
    { isActive, peekHandleKey },
    { minimizeToPeekHandle, expandPeekHandle },
  ];
};

export { usePeekHandle };
